const parseGameConfig = (trigger) => {
    const rawConfig = trigger.dataset.gameConfig;

    if (!rawConfig) {
        return null;
    }

    try {
        return JSON.parse(rawConfig);
    } catch (error) {
        return null;
    }
};

const createBubbleMarkup = (bubble, isAnswered, isSelected) => `
    <button
        class="symptoms-match__bubble ${bubble.className}${isAnswered ? ' is-answered' : ''}${isSelected ? ' is-selected' : ''}"
        type="button"
        draggable="${isAnswered ? 'false' : 'true'}"
        data-symptoms-bubble
        data-bubble-id="${bubble.id}"
        ${isAnswered ? 'hidden' : ''}
        aria-pressed="${isSelected ? 'true' : 'false'}"
    >
        <span class="symptoms-match__bubble-label">${bubble.name}</span>
    </button>
`;

const createSymptomMarkup = (symptom, state, inlineSvg) => {
    const matchedBubble = state.matches[symptom.id];
    const bubble = matchedBubble ? state.bubblesById[matchedBubble] : null;
    const bubbleMarkup = bubble
        ? `
            <div class="symptoms-match__placed-bubble ${bubble.className}" data-bubble-id="${bubble.id}">
                <img src="${symptom.bubbleImageUrl}" alt="" loading="lazy">
                <span class="symptoms-match__placed-label">${bubble.name}</span>
                <img class="symptoms-match__tick" src="${state.tickImageUrl}" alt="" loading="lazy">
            </div>
        `
        : '';

    return `
        <div
            class="symptoms-match__target ${symptom.className}${matchedBubble ? ' is-correct' : ''}"
            data-symptom-id="${symptom.id}"
            tabindex="0"
            role="button"
            aria-label="Match ${bubble ? bubble.name.replace(/<br>/g, ' ') : 'symptom'}"
        >
            ${bubbleMarkup}
            <div class="symptoms-match__svg">${inlineSvg}</div>
        </div>
    `;
};

const colorCorrectFill = (wrapper, color) => {
    wrapper.querySelectorAll('.correct-fill').forEach((node) => {
        node.style.fill = color;
    });
};

const fetchSvgText = async (url) => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to load ${url}`);
    }

    return response.text();
};

const buildState = (config, resetLabel) => {
    const bubblesById = Object.fromEntries(config.bubbles.map((bubble) => [bubble.id, bubble]));
    const symptomsById = Object.fromEntries(config.symptoms.map((symptom) => [symptom.id, symptom]));

    return {
        bubbles: config.bubbles,
        bubblesById,
        symptoms: config.symptoms,
        symptomsById,
        matches: {},
        selectedBubbleId: null,
        resetLabel,
        tickImageUrl: config.tickImageUrl,
        svgCache: {},
    };
};

const renderGame = (stage, state) => {
    const bubbleMarkup = state.bubbles
        .map((bubble) => createBubbleMarkup(bubble, Boolean(state.matches[bubble.id]), state.selectedBubbleId === bubble.id))
        .join('');

    const symptomMarkup = state.symptoms
        .map((symptom) => createSymptomMarkup(symptom, state, state.svgCache[symptom.id] || ''))
        .join('');

    stage.innerHTML = `
        <div class="symptoms-match">
            <h3 class="symptoms-match__heading">Can you match the symptoms?</h3>
            <p class="symptoms-match__copy">Select and drag the word to match the image.</p>

            <div class="symptoms-match__bubbles">${bubbleMarkup}</div>

            <div class="symptoms-match__drag-prompt">
                <p>Drag</p>
                <img src="${state.arrowImageUrl}" alt="" loading="lazy">
            </div>

            <div class="symptoms-match__targets">${symptomMarkup}</div>

            <div class="symptoms-match__actions">
                <button class="symptoms-match__reset" type="button" data-symptoms-reset>${state.resetLabel}</button>
            </div>
        </div>
    `;

    state.symptoms.forEach((symptom) => {
        if (state.matches[symptom.id]) {
            const target = stage.querySelector(`[data-symptom-id="${symptom.id}"] .symptoms-match__svg`);

            if (target) {
                colorCorrectFill(target, symptom.correctColor);
            }
        }
    });
};

const attachInteractions = (stage, state) => {
    stage.querySelectorAll('[data-symptoms-bubble]').forEach((bubbleNode) => {
        bubbleNode.addEventListener('dragstart', (event) => {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', bubbleNode.dataset.bubbleId);
        });

        bubbleNode.addEventListener('click', () => {
            const bubbleId = Number(bubbleNode.dataset.bubbleId);
            state.selectedBubbleId = state.selectedBubbleId === bubbleId ? null : bubbleId;
            renderGame(stage, state);
            attachInteractions(stage, state);
        });
    });

    stage.querySelectorAll('[data-symptom-id]').forEach((targetNode) => {
        const applyMatch = (bubbleId) => {
            const symptomId = Number(targetNode.dataset.symptomId);

            if (bubbleId !== symptomId || state.matches[symptomId]) {
                return;
            }

            state.matches[symptomId] = bubbleId;
            state.selectedBubbleId = null;
            renderGame(stage, state);
            attachInteractions(stage, state);
        };

        targetNode.addEventListener('dragover', (event) => {
            event.preventDefault();
        });

        targetNode.addEventListener('drop', (event) => {
            event.preventDefault();
            applyMatch(Number(event.dataTransfer.getData('text/plain')));
        });

        targetNode.addEventListener('click', () => {
            if (state.selectedBubbleId) {
                applyMatch(state.selectedBubbleId);
            }
        });

        targetNode.addEventListener('keydown', (event) => {
            if ((event.key === 'Enter' || event.key === ' ') && state.selectedBubbleId) {
                event.preventDefault();
                applyMatch(state.selectedBubbleId);
            }
        });
    });

    const resetButton = stage.querySelector('[data-symptoms-reset]');

    if (resetButton) {
        resetButton.addEventListener('click', () => {
            state.matches = {};
            state.selectedBubbleId = null;
            renderGame(stage, state);
            attachInteractions(stage, state);
        });
    }
};

const loadSvgCache = async (state) => {
    const results = await Promise.all(
        state.symptoms.map(async (symptom) => {
            const svgText = await fetchSvgText(symptom.imageUrl);

            return [symptom.id, svgText];
        })
    );

    state.svgCache = Object.fromEntries(results);
};

const startGame = async (trigger, component) => {
    const stage = component.querySelector('[data-symptoms-game-stage]');
    const instructions = component.querySelector('[data-symptoms-game-instructions]');
    const gameConfig = parseGameConfig(trigger);

    if (!stage || !instructions || !gameConfig || stage.dataset.isLoaded === 'true') {
        return;
    }

    const state = buildState(gameConfig, trigger.dataset.resetLabel || 'Reset Game');
    state.arrowImageUrl = gameConfig.arrowImageUrl;

    trigger.disabled = true;
    trigger.classList.add('is-loading');

    try {
        await loadSvgCache(state);

        instructions.hidden = true;
        stage.hidden = false;
        stage.dataset.isLoaded = 'true';
        renderGame(stage, state);
        attachInteractions(stage, state);
    } catch (error) {
        trigger.disabled = false;
    } finally {
        trigger.classList.remove('is-loading');
    }
};

const initCanYouMatchTheSymptoms = () => {
    const triggers = document.querySelectorAll('[data-symptoms-game-start]');

    if (!triggers.length) {
        return;
    }

    triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
            const component = trigger.closest('[data-symptoms-game-component]');

            if (!component) {
                return;
            }

            startGame(trigger, component);
        });
    });
};

export default initCanYouMatchTheSymptoms;
